import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { useSbaCalendarEventDetail } from '@/hooks/useSbaCalendars'

interface SeasonalMilestonesTableProps {
  calendarId: string
  eventId: string
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function fmtLong(iso: string | null | undefined) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  if (!y) return '—'
  return `${MONTHS[(m ?? 1) - 1]} ${d ?? 1}, ${y}`
}

export function SeasonalMilestonesTable({
  calendarId,
  eventId,
}: SeasonalMilestonesTableProps) {
  const {
    data: detail,
    isLoading,
    isError,
  } = useSbaCalendarEventDetail(calendarId, eventId)

  const milestones = useMemo(
    () =>
      [...(detail?.milestones ?? [])].sort(
        (a, b) => a.display_order - b.display_order
      ),
    [detail]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading milestones…
      </div>
    )
  }

  if (isError) {
    return (
      <p className="rounded-lg border border-dashed border-destructive/40 p-6 text-center text-sm text-destructive">
        Failed to load milestones for this event.
      </p>
    )
  }

  if (milestones.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No milestones for this event.
      </p>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/50">
          <tr className="text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 text-left font-semibold">Milestone</th>
            <th className="px-3 py-2 text-left font-semibold">Date</th>
          </tr>
        </thead>
        <tbody>
          {milestones.map(m => (
            <tr key={m.id} className="border-t border-border/60">
              <td className="px-3 py-2 font-medium text-foreground">
                {m.name}
              </td>
              <td className="px-3 py-2 tabular-nums text-muted-foreground">
                {m.specific_date ? (
                  <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                    {fmtLong(m.specific_date)}
                  </span>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
